import {
  hashKey,
  experimental_streamedQuery as streamedQuery,
  useQuery,
} from '@tanstack/react-query';
import { useDeferredValue, useMemo } from 'react';
import { appendBatch, createComparator, createMergeReducer, DEFAULT_SORT_ORDER } from './reducers';

export const STREAM_REFETCH_MODE = {
  // Clear existing data and stream the new result in from scratch.
  RESET: 'reset',
  // Keep existing data and add the new batches to it.
  APPEND: 'append',
  // Keep showing existing data until the new stream completes, then swap it in.
  REPLACE: 'replace',
};

const EMPTY_ITEMS = Object.freeze([]);

const hasItems = (value) =>
  Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null;

// TanStack stores each streamed batch as a manual update, so cancelling a stream (closing the view
// or changing the key) restores the last batch and keeps it as a fresh, successful result. Drop
// that partial result once nothing observes it, so the next mount downloads everything again.
const dropCancelledResult = (client, queryKey) => {
  setTimeout(() => {
    const cache = client.getQueryCache();
    const query = cache.find({ queryKey, exact: true });
    if (query && query.getObserversCount() === 0 && query.state.fetchStatus === 'idle') {
      cache.remove(query);
    }
  }, 0);
};

async function* onEachBatch(batches, callback) {
  for await (const batch of await batches) {
    callback();
    yield batch;
  }
}

// Wraps the stream so a cancelled download that already wrote a partial result to the cache is
// dropped. A `replace` refetch writes nothing until it completes, so its cached data is kept.
const dropPartialResultOnCancel = (batches, { signal, queryKey, client, refetchMode }) => {
  const query = client.getQueryCache().find({ queryKey, exact: true });
  const keepsPreviousData =
    refetchMode === STREAM_REFETCH_MODE.REPLACE && query?.state.data !== undefined;
  if (keepsPreviousData) return batches;

  let hasPartialResult = false;
  signal.addEventListener(
    'abort',
    () => {
      if (hasPartialResult) dropCancelledResult(client, queryKey);
    },
    { once: true }
  );
  return onEachBatch(batches, () => {
    hasPartialResult = true;
  });
};

/**
 * Generic hook for streaming (NDJSON) endpoints. Data grows as batches arrive instead of the
 * component waiting for the whole response.
 *
 * The single place that depends on TanStack's `experimental_streamedQuery`, so a future API change
 * only needs to be handled here.
 *
 * @param {Array} queryKey Must include every input of the request.
 * @param {(context: { signal: AbortSignal, queryKey: Array }) => AsyncIterable<Array>} streamFn
 *   Returns an async iterable of batches, e.g. `streamNdjsonRequest(...)`. Pass `signal` to the
 *   request so it is aborted when the component unmounts or the key changes.
 * @param {Object} [options] Any `useQuery` option, plus:
 * @param {string} [options.sortBy] Field to keep the data sorted by as batches arrive (each batch is
 *   merged into the sorted list in linear time). Ignored when `reducer` is given.
 * @param {'asc'|'desc'} [options.sortOrder='desc'] See `SORT_ORDER` / `DEFAULT_SORT_ORDER`.
 * @param {(item: *) => *} [options.getSortValue] Custom sort value instead of `item[sortBy]`.
 * @param {(item: *) => *} [options.getKey] De-duplicates items by key (later items replace earlier).
 * @param {(items: Array, batch: Array) => Array} [options.reducer] Fully custom reducer; overrides
 *   `sortBy`/`getKey`. Default: append batches in arrival order.
 * @param {Array} [options.initialValue] Data before the first batch / for an empty stream.
 * @param {'reset'|'append'|'replace'} [options.refetchMode] See `STREAM_REFETCH_MODE`.
 * @param {(item: *) => boolean} [options.filter] Keeps only matching items in `data`. Pass a stable
 *   function (module level or memoized); it re-runs whenever it changes.
 * @param {boolean} [options.deferred=true] Renders large batches with `useDeferredValue`, so the
 *   view stays responsive while thousands of rows arrive.
 * @returns The `useQuery` result, adapted so a component written for a normal request works as-is:
 *   - `data`: the items received so far (filtered, sorted, de-duplicated). Never `undefined` while
 *     the default array `initialValue` is used.
 *   - `isLoading`: nothing to show yet and the request is still running (show a skeleton).
 *   - `isError`: the request failed before anything could be shown.
 *   - `isEmpty`: finished successfully with nothing to show.
 *   - `isStreaming`: rows are shown and more are still arriving (optional "loading more" hint).
 *   - `isPartial`: failed after some rows arrived; `data` keeps them (optional "incomplete" note).
 *   - `isStreamComplete`: finished successfully.
 *   The raw TanStack state stays available via `status`, `fetchStatus` and `error`.
 */
export const useStreamQuery = (queryKey, streamFn, options = {}) => {
  const {
    reducer: customReducer,
    sortBy,
    sortOrder = DEFAULT_SORT_ORDER,
    getSortValue,
    getKey,
    initialValue = EMPTY_ITEMS,
    refetchMode = STREAM_REFETCH_MODE.RESET,
    filter,
    deferred = true,
    ...queryOptions
  } = options;

  const reducer = useMemo(() => {
    if (customReducer) return customReducer;
    if (!sortBy && !getSortValue && !getKey) return appendBatch;
    const compare =
      sortBy || getSortValue
        ? createComparator({ sortBy, order: sortOrder, getSortValue })
        : undefined;
    return createMergeReducer({ getKey, compare });
  }, [customReducer, sortBy, sortOrder, getSortValue, getKey]);

  const query = useQuery({
    queryKey,
    queryFn: streamedQuery({
      // Reading `signal` here (even if `streamFn` ignores it) is what makes TanStack abort the request
      // and stop the stream when the component unmounts or the key changes.
      streamFn: ({ signal, queryKey: key, client }) =>
        dropPartialResultOnCancel(streamFn({ signal, queryKey: key }), {
          signal,
          queryKey: key,
          client,
          refetchMode,
        }),
      reducer,
      initialValue,
      refetchMode,
    }),
    // A retry would restart the stream from the beginning; let callers opt in explicitly.
    retry: false,
    // Every batch produces a new array of new objects, so deep structural comparison is wasted work.
    structuralSharing: false,
    ...queryOptions,
  });

  const rawData = query.data === undefined ? initialValue : query.data;
  const visibleData = useMemo(
    () => (filter && Array.isArray(rawData) ? rawData.filter(filter) : rawData),
    [rawData, filter]
  );
  // Defer large renders, but never return the previous key's rows while the deferred value catches up.
  const keyHash = hashKey(queryKey);
  const current = useMemo(() => ({ keyHash, items: visibleData }), [keyHash, visibleData]);
  const deferredCurrent = useDeferredValue(current);
  const data =
    deferred && deferredCurrent.keyHash === keyHash ? deferredCurrent.items : visibleData;

  const hasReceived = hasItems(visibleData);
  const hasRendered = hasItems(data);
  // A deferred render is still catching up with the received rows.
  const isCatchingUp = data !== visibleData;
  const { isFetching, isSuccess } = query;
  const isBusy = isFetching || isCatchingUp;
  const hasFailed = query.isError;

  return {
    ...query,
    data,
    isLoading: isBusy && !hasRendered,
    isError: hasFailed && !hasReceived,
    isEmpty: isSuccess && !isBusy && !hasReceived,
    isStreaming: isBusy && hasRendered,
    isPartial: hasFailed && hasReceived,
    isStreamComplete: isSuccess && !isBusy,
  };
};
